import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Alert } from 'antd';
import _ from 'lodash';
import type { TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { createApiGateway } from '../../../apiGateway';
import { useFinancesData } from '../../../hooks/useFinancesData';
import { type SelectOption, type FinancesTableRow } from '../../../types/finances';
import { FinancesFiltersComponent } from './components/FinancesFilters';
import { FinancesTable } from './components/FinancesTable';
import { useExportFinancesCsv } from './hooks/useExportFinancesCsv';

type TableChangeHandler = NonNullable<TableProps<FinancesTableRow>['onChange']>;

const apiGateway = createApiGateway();

interface User {
  id: number;
  firstName: string;
}

export const NewFinancesPage: React.FC = () => {
  const {
    pendingFilters,
    appliedFilters,
    updatePendingFilters,
    applyFilters,
    hasUnappliedChanges,
    warning,
    tableData,
    loading,
    error,
    shouldPaginate,
    pagination,
    totalCount,
    pageSizeOptions,
    changePage,
    changeSort,
    order,
  } = useFinancesData();
  const [companies, setCompanies] = useState<SelectOption<number>[]>([]);
  const { exportCsv } = useExportFinancesCsv();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const allUsers = await apiGateway.users.getAll({ allowedRoles: ['user'] });

        const companyOptions = _.sortBy(
          (allUsers as User[]).map((user: User) => ({
            value: user.id,
            label: `[${user.id}] ${user.firstName}`,
          })),
          'value'
        );

        setCompanies(companyOptions);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };

    fetchCompanies();
  }, []);

  const pageSizeOptionsAsString = useMemo(() => pageSizeOptions.map((size) => size.toString()), [pageSizeOptions]);

  const tablePaginationConfig = useMemo<false | TablePaginationConfig>(() => {
    if (!shouldPaginate) {
      return false;
    }

    return {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: totalCount,
      showSizeChanger: true,
      hideOnSinglePage: true,
      pageSizeOptions: pageSizeOptionsAsString,
    };
  }, [shouldPaginate, pagination.current, pagination.pageSize, totalCount, pageSizeOptionsAsString]);

  const handleTableChange = useCallback<TableChangeHandler>(
    (tablePagination, _filters, sorter) => {
      const normalizedSorter = Array.isArray(sorter)
        ? (sorter[0] as SorterResult<FinancesTableRow> | undefined)
        : ((sorter as SorterResult<FinancesTableRow>) ?? undefined);

      const sorterColumnKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
      const sorterOrder =
        normalizedSorter?.order === 'ascend' ? 'ASC' : normalizedSorter?.order === 'descend' ? 'DESC' : undefined;

      const sortChanged =
        sorterColumnKey && sorterColumnKey !== 'index'
          ? sorterOrder
            ? sorterColumnKey !== order.by || sorterOrder !== order.direction
            : Boolean(order.by)
          : false;

      if (sortChanged) {
        const nextPage = shouldPaginate ? (tablePagination?.current ?? 1) : undefined;
        const nextPageSize = shouldPaginate ? (tablePagination?.pageSize ?? pagination.pageSize) : undefined;

        if (sorterColumnKey && sorterColumnKey !== 'index' && sorterOrder) {
          changeSort({
            columnKey: sorterColumnKey,
            direction: sorterOrder,
            page: nextPage,
            pageSize: nextPageSize,
          });
        } else {
          changeSort({
            page: nextPage,
            pageSize: nextPageSize,
          });
        }
        return;
      }

      if (shouldPaginate && tablePagination && typeof tablePagination === 'object') {
        const nextPage = tablePagination.current ?? 1;
        const nextPageSize = tablePagination.pageSize ?? pagination.pageSize;

        if (nextPage !== pagination.current || nextPageSize !== pagination.pageSize) {
          changePage(nextPage, nextPageSize);
        }
      }
    },
    [shouldPaginate, changePage, changeSort, pagination.current, pagination.pageSize, order.by, order.direction]
  );

  return (
    <div className="xchange-page">
      <FinancesFiltersComponent
        filters={pendingFilters}
        onFiltersChange={updatePendingFilters}
        companies={companies}
        onApplyFilters={applyFilters}
        loading={loading}
        hasUnappliedChanges={hasUnappliedChanges}
        onExportCsv={() =>
          exportCsv({
            filters: appliedFilters,
            order,
            pagination,
            shouldPaginate,
          })
        }
        exportDisabled={!tableData || tableData.length === 0}
      />

      {warning && (
        <Alert title="Notice" description={warning} type="warning" showIcon style={{ marginBottom: '16px' }} />
      )}

      {error && <Alert title="Error" description={error} type="error" showIcon style={{ marginBottom: '16px' }} />}

      <FinancesTable
        data={tableData}
        loading={loading}
        filters={appliedFilters}
        pagination={tablePaginationConfig}
        onTableChange={handleTableChange}
        sortState={order}
      />
    </div>
  );
};
